import { X, BookOpen, Shield, Radio, Users, Star, Compass, MessageSquare, MapPin, ShoppingBag, Award } from 'lucide-react';

export default function AyudaScreen({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[300] bg-bg-dark flex flex-col items-center h-[100dvh] w-full overflow-hidden text-left bg-gradient-to-b from-[#0f172a] to-black">
      {/* Header Fijo */}
      <div className="w-full bg-black/80 backdrop-blur-md border-b border-white/5 pt-8 px-5 pb-4 flex justify-between items-center shadow-2xl relative z-10 shrink-0">
        <div className="flex items-center gap-3 relative z-10">
          <div className="bg-primary-cyan/10 p-2 rounded-xl border border-primary-cyan/30 shadow-[0_0_15px_rgba(0,168,255,0.15)]">
            <BookOpen className="w-6 h-6 text-primary-cyan" />
          </div>
          <div>
            <h2 className="text-[1.15rem] font-black text-white leading-tight uppercase tracking-wide">Manual de App</h2>
            <p className="text-[10px] text-primary-cyan font-extrabold tracking-wider uppercase drop-shadow-md">Guía del Rider</p>
          </div>
        </div>
        <button 
          onClick={onClose}
          className="p-2.5 bg-white/5 hover:bg-white/10 rounded-full transition-all active:scale-95 border border-white/10"
        >
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Contenido (Scrollable) */}
      <div className="flex-1 overflow-y-auto w-full p-5 pb-24 space-y-6">
        
        {/* Intro */}
        <div className="bg-slate-800/40 border border-white/5 rounded-2xl p-5 shadow-lg relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary-cyan/10 blur-3xl pointer-events-none rounded-full translate-x-1/2 -translate-y-1/2"></div>
          <p className="text-[13px] text-slate-300 font-medium leading-relaxed relative z-10">
            <strong className="text-white text-sm font-black">Gravity Bike Pro</strong> es el ecosistema definitivo para tu bicicleta de gravedad y rodadas de velocidad. Una bóveda digital diseñada para conectar la hermandad de gravity riders mediante telemetría en tiempo real, rescate en pista, control de componentes e integración con la comunidad de descenso libre.
          </p>
        </div>

        {/* Sección 1: Gravity */}
        <div>
          <h3 className="text-[13px] font-black text-white flex items-center gap-2 mb-3 uppercase tracking-wider">
            <Compass className="w-5 h-5 text-primary-cyan" /> 1. Gravity (Velocímetro y Odómetro)
          </h3>
          <div className="bg-black/40 border border-white/5 rounded-2xl p-5 shadow-inner">
            <ul className="text-[12.5px] text-slate-400 space-y-3 font-medium">
              <li className="flex gap-2"><span className="text-primary-cyan font-bold">•</span> Monitorea tu velocidad actual, velocidad máxima y acelerómetro en descensos libres en tiempo real.</li>
              <li className="flex gap-2"><span className="text-primary-cyan font-bold">•</span> Al finalizar un descenso, genera tu tarjeta de telemetría en alta definición con el color de tu categoría ganada: <strong className="text-yellow-400">Oro (&gt;=90 km/h)</strong>, <strong className="text-cyan-400">Cyan (&gt;=80 km/h)</strong> o <strong className="text-orange-400">Bronce (&lt;80 km/h)</strong>.</li>
              <li className="flex gap-2"><span className="text-primary-cyan font-bold">•</span> Registra y sincroniza de forma segura tu kilometraje real acumulado para subir en la tabla de clasificación.</li>
            </ul>
          </div>
        </div>

        {/* Sección 2: Muro Bike */}
        <div>
          <h3 className="text-[13px] font-black text-white flex items-center gap-2 mb-3 uppercase tracking-wider">
            <MessageSquare className="w-5 h-5 text-blue-400" /> 2. Muro Bike (La Red Social)
          </h3>
          <div className="bg-black/40 border border-white/5 rounded-2xl p-5 shadow-inner">
            <ul className="text-[12.5px] text-slate-400 space-y-3 font-medium">
              <li className="flex gap-2"><span className="text-blue-400 font-bold">•</span> El espacio oficial de la hermandad. Publica fotos de tus mejores descensos, comparte tus spots favoritos de velocidad y publica tus tarjetas de telemetría directamente desde el velocímetro.</li>
              <li className="flex gap-2"><span className="text-blue-400 font-bold">•</span> Da "Me Gusta", comenta y reacciona a las hazañas de otros riders de la comunidad en tiempo real.</li>
              <li className="flex gap-2"><span className="text-blue-400 font-bold">•</span> Mantén una convivencia sana y reporta contenido inadecuado para su depuración inmediata.</li>
            </ul>
          </div>
        </div>

        {/* Sección 3: Rutas */}
        <div>
          <h3 className="text-[13px] font-black text-white flex items-center gap-2 mb-3 uppercase tracking-wider">
            <MapPin className="w-5 h-5 text-green-400" /> 3. Rutas
          </h3>
          <div className="bg-black/40 border border-white/5 rounded-2xl p-5 shadow-inner">
            <ul className="text-[12.5px] text-slate-400 space-y-3 font-medium">
              <li className="flex gap-2"><span className="text-green-400 font-bold">•</span> Explora los destinos oficiales del club o planifica tus descensos compartiéndolos con el parche.</li>
              <li className="flex gap-2"><span className="text-green-400 font-bold">•</span> Abre los puntos de encuentro y trazado de pistas de forma automática en Google Maps o Waze.</li>
              <li className="flex gap-2"><span className="text-green-400 font-bold">•</span> Confirma tu asistencia y mantén el control de la lista de riders que participarán en la rodada.</li>
            </ul>
          </div>
        </div>

        {/* Sección 4: S.O.S */}
        <div>
          <h3 className="text-[13px] font-black text-danger-red flex items-center gap-2 mb-3 uppercase tracking-wider">
            <Radio className="w-5 h-5 text-red-500 drop-shadow-[0_0_8px_rgba(239,68,68,0.5)]" /> 4. S.O.S (Seguridad en Ruta)
          </h3>
          <div className="bg-black/40 border border-white/5 rounded-2xl p-5 shadow-inner">
            <ul className="text-[12.5px] text-slate-400 space-y-3 font-medium">
              <li className="flex gap-2"><span className="text-red-500 font-bold">•</span> Botón de pánico de alta visibilidad diseñado para activarse ante caídas en pista o emergencias médicas durante un descenso.</li>
              <li className="flex gap-2"><span className="text-red-500 font-bold">•</span> El sistema emitirá alertas geolocalizadas y balizas en tiempo real a tus contactos SOS designados.</li>
              <li className="flex gap-2"><span className="text-red-500 font-bold">•</span> Notificaciones instantáneas romperán el silencio de sus teléfonos para ir en tu auxilio de forma prioritaria.</li>
            </ul>
          </div>
        </div>

        {/* Sección 5: Parche */}
        <div>
          <h3 className="text-[13px] font-black text-purple-400 flex items-center gap-2 mb-3 uppercase tracking-wider">
            <Users className="w-5 h-5 text-purple-400" /> 5. Parche (Escuadrones y Rankings)
          </h3>
          <div className="bg-black/40 border border-white/5 rounded-2xl p-5 shadow-inner">
            <ul className="text-[12.5px] text-slate-400 space-y-3 font-medium">
              <li className="flex gap-2"><span className="text-purple-400 font-bold">•</span> Crea un club o únete a un escuadrón de gravedad activo para chatear, coordinar rodadas y compartir ubicación en tiempo real.</li>
              <li className="flex gap-2"><span className="text-purple-400 font-bold">•</span> Compite en la tabla de clasificación global (Ranking) acumulando kilómetros reales en descensos oficiales.</li>
              <li className="flex gap-2"><span className="text-purple-400 font-bold">•</span> Gana puntos de honor para subir de categoría y destacar entre los mejores pilotos de la red.</li>
            </ul>
          </div>
        </div>

        {/* Sección 6: Bikemarket */}
        <div>
          <h3 className="text-[13px] font-black text-amber-500 flex items-center gap-2 mb-3 uppercase tracking-wider">
            <ShoppingBag className="w-5 h-5 text-amber-500" /> 6. Bikemarket (Compra y Venta)
          </h3>
          <div className="bg-black/40 border border-white/5 rounded-2xl p-5 shadow-inner">
            <ul className="text-[12.5px] text-slate-400 space-y-3 font-medium">
              <li className="flex gap-2"><span className="text-amber-500 font-bold">•</span> El mercado especializado para la comunidad. Publica fotos y detalles de bicicletas de gravedad completas, marcos de alto rendimiento, sistemas de freno reforzados y accesorios deportivos.</li>
              <li className="flex gap-2"><span className="text-amber-500 font-bold">•</span> Contacta de forma directa e instantánea a los vendedores para concretar transacciones de forma segura y transparente.</li>
            </ul>
          </div>
        </div>

        {/* Sección 7: VIP */}
        <div>
          <h3 className="text-[13px] font-black text-yellow-500 flex items-center gap-2 mb-3 uppercase tracking-wider">
            <Award className="w-5 h-5 text-yellow-500" /> 7. VIP (Membresía Oro y Bóveda Legal)
          </h3>
          <div className="bg-black/40 border border-white/5 rounded-2xl p-5 shadow-inner">
            <ul className="text-[12.5px] text-slate-400 space-y-3 font-medium">
              <li className="flex gap-2"><span className="text-yellow-500 font-bold">•</span> Accede al nivel premium Oro/VIP de Gravity Bike para desbloquear características exclusivas, temas personalizados de color y máxima distinción.</li>
              <li className="flex gap-2"><span className="text-yellow-500 font-bold">•</span> Habilita la Bóveda de Documentos Legales para respaldar de manera segura facturas de tus marcos, garantías deportivas y carnés de pista.</li>
            </ul>
          </div>
        </div>

        {/* Privacidad */}
        <div>
          <h3 className="text-[13px] font-black text-primary-cyan flex items-center gap-2 mb-3 uppercase tracking-wider">
            <Shield className="w-5 h-5 text-primary-cyan" /> Privacidad Blindada
          </h3>
          <div className="bg-black/40 border border-white/5 rounded-2xl p-5 shadow-inner">
            <p className="text-[12.5px] text-slate-400 font-medium mb-4 leading-relaxed">
              Tu seguridad no es solo física, también es digital. Resguardamos tus datos bajo los más altos estándares legales de Colombia.
            </p>
            <a 
              href="https://pikishub.com/gravitybike/privacidad.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex justify-center items-center gap-2 w-full py-3 bg-primary-cyan/10 hover:bg-primary-cyan/20 border border-primary-cyan/30 rounded-xl transition-all active:scale-95"
            >
              <span className="text-[11px] font-black text-primary-cyan uppercase tracking-wider">Ver Políticas de Privacidad</span>
              <Star className="w-3 h-3 text-primary-cyan" />
            </a>
          </div>
        </div>

        <div className="pt-8 pb-4 text-center opacity-40">
          <div className="w-10 h-1 bg-white/10 mx-auto rounded-full mb-3"></div>
          <p className="text-[9px] text-slate-500 font-black uppercase tracking-widest leading-none">Desarrollado con Honor por PIKIS HUB</p>
          <p className="text-[8px] text-slate-600 font-bold uppercase tracking-widest mt-2">© 2026 GRAVITY BIKE</p>
        </div>

      </div>
    </div>
  );
}
